import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useSiteId } from "../hooks/useSiteId";
import { useLiveDataAvailability } from "../hooks/useLiveDataAvailability";
import { useCollapsedPanel } from "../hooks/useCollapsedPanel";
import { Reveal } from "../components/Reveal";
import { useSiteStore } from "../stores/siteStore";
import { useUIStore } from "../stores/uiStore";
import { listZones, simulateRelocation, simulateTaskMix, type TaskMixSim } from "../api/rest";
import { fmtDistance, fmtHours, fmtMoney, fmtNum } from "../app/format";
import styles from "./Panel.module.css";

/**
 * ADR 290 (movement-cost slice 3): the relocation what-if, on /live where the
 * pallets being discussed are on screen. Select by expiry and/or product,
 * pick a staging/dock zone, simulate — the scene paints the loaded-leg routes
 * (SimulationRoutesLayer) and emphasizes the destination zone; this panel
 * carries the money. Stateless server-side; the result lives in uiStore
 * (warehouse-keyed) until cleared or re-run.
 *
 * The occupancy truth is pinned to the run the user is LOOKING AT: the run
 * picker's single selection when set, else the warehouse's latest (ADR 289).
 *
 * ADR 227 gate: the card exists only when a simulation is actually possible
 * (inventory capability, a staging/dock zone, a paintable run) — a panel
 * whose only content is an excuse is a control bound to nothing.
 */
export function SimulationPanel() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const siteId = useSiteId();
  const warehouseId = useSiteStore((s) => s.activeWarehouseId);
  const hasInventory = useSiteStore((s) => s.hasCapability("inventory"));
  const runSelection = useUIStore((s) => s.runSelection);
  // ADR 355: the occasional tool of the column starts folded — Camadas (and
  // its paint modes) must not live below the fold because of it.
  const [collapsed, toggleCollapsed] = useCollapsedPanel("simulation", true);
  // ADR 351: the finder's matched set is a first-class selection source — the
  // count is the cheap subscription; the addresses are read via getState() at
  // submit so a keystroke in the search box doesn't re-render this panel's form.
  const searchCount = useUIStore((s) => s.productMatchCount);
  const simulation = useUIStore((s) => s.simulation);
  const setSimulation = useUIStore((s) => s.setSimulation);
  const clearSimulation = useUIStore((s) => s.clearSimulation);

  const [mode, setMode] = useState<"relocation" | "taskMix">("relocation");
  const [useSearch, setUseSearch] = useState(false);
  const [expiryBefore, setExpiryBefore] = useState("");
  const [product, setProduct] = useState("");
  const [pickedZoneId, setPickedZoneId] = useState("");
  const [forklifts, setForklifts] = useState("1");
  // ADR 305 inputs. maxInterleave is geography (how far a pairing may reach);
  // rateCap is dispatch (how often the WMS actually pairs). Kept local rather
  // than in uiStore: unlike the relocation result this one paints nothing on
  // the scene, so there is no cross-component state to share.
  const [maxInterleaveM, setMaxInterleaveM] = useState("25");
  const [rateCap, setRateCap] = useState("");
  const [taskMix, setTaskMix] = useState<TaskMixSim | null>(null);

  // Same key as useLiveDataAvailability/ZonePanel — one fetch per warehouse.
  const zones = useQuery({
    queryKey: ["zones", siteId, warehouseId],
    queryFn: () => listZones(siteId, warehouseId!),
    enabled: !!siteId && !!warehouseId,
    staleTime: 60_000,
  });
  // ADR 353: every zone is a valid destination — aisle-to-aisle moves are as
  // real a cost question as clearing to staging. Staging/dock sort first
  // because they remain the common answer, not the only one.
  const destinations = [...(zones.data ?? [])].sort((a, b) => {
    const pri = (z: { kind: string }) => (z.kind === "staging" || z.kind === "dock" ? 0 : 1);
    return pri(a) - pri(b) || a.id.localeCompare(b.id);
  });
  // ADR 298 class: the panel is not remounted when the active warehouse changes, so a
  // raw useState would keep pointing at the zone picked in the PREVIOUS warehouse — the
  // select renders blank while `canRun` stays true, and Simular would post a zone this
  // warehouse does not have. Derived with a membership check, the way every admin picker
  // on this codebase does it: an invalid pick self-heals on the next render, no effect.
  const destinationZoneId =
    pickedZoneId && destinations.some((z) => z.id === pickedZoneId) ? pickedZoneId : "";

  // Cache-shared with LayerPanel in the same column — no extra requests.
  const available = useLiveDataAvailability(siteId, warehouseId);

  const fromSearchNow = () => useSearch && useUIStore.getState().productMatchCount != null;
  const run = useMutation({
    mutationFn: () => {
      const runId = runSelection && runSelection.warehouseId === warehouseId
        && runSelection.runIds.length === 1 ? runSelection.runIds[0] : null;
      const matches = useUIStore.getState().productMatches;
      return simulateRelocation(siteId, warehouseId!, {
        selection: fromSearchNow() && matches
          ? { addresses: matches }
          : { expiryBefore: expiryBefore || null, product: product.trim() || null },
        destinationZoneId,
        forklifts: Math.max(1, Number(forklifts.replace(",", ".")) || 1),
        runId,
      });
    },
    onSuccess: (r) => setSimulation(warehouseId!, destinationZoneId, r),
  });

  const runTaskMix = useMutation({
    mutationFn: () => {
      const runId = runSelection && runSelection.warehouseId === warehouseId
        && runSelection.runIds.length === 1 ? runSelection.runIds[0] : null;
      const cap = rateCap.trim() === "" ? null
        : Math.min(1, Math.max(0, Number(rateCap.replace(",", ".")) / 100));
      return simulateTaskMix(siteId, warehouseId!, {
        dockZoneId: destinationZoneId,
        maxInterleaveM: Number(maxInterleaveM.replace(",", ".")) || 25,
        interleaveRateCap: Number.isFinite(cap as number) ? cap : null,
        forklifts: Math.max(1, Number(forklifts.replace(",", ".")) || 1),
        runId,
      });
    },
    onSuccess: (r) => setTaskMix(r),
  });

  // ADR 227: no zone or no run means no simulation is possible — the panel
  // does not exist rather than explaining itself. "Create a zone first" is
  // the zones admin's story, not a permanent placeholder here — except under
  // Modo diagnóstico (ADR 356), where the gate lists what it needs.
  if (!warehouseId || !hasInventory || !available.runs || destinations.length === 0) {
    return <Reveal label={t("simulation.title")} reqs={[
      { label: t("req.warehouse"), ok: !!warehouseId },
      { label: t("req.inventoryCapability"), ok: hasInventory },
      { label: t("req.runs"), ok: !!available.runs },
      { label: t("req.zones"), ok: destinations.length > 0 },
    ]} />;
  }
  // The toggle only binds while a search is actually active; a cleared search
  // falls back to the panel's own fields rather than a dead checkbox.
  const fromSearch = useSearch && searchCount != null;
  const canRun = !!destinationZoneId && !run.isPending
    && (fromSearch ? searchCount > 0 : (!!expiryBefore || !!product.trim()));
  const result = simulation && simulation.warehouseId === warehouseId ? simulation.result : null;

  const inputStyle: React.CSSProperties = {
    width: "100%", padding: "4px 6px", marginTop: 2,
    background: "var(--op-surface)", color: "var(--op-ink-900)",
    border: "1px solid var(--op-line)", borderRadius: 4,
  };

  return (
    <aside className={styles.panel} aria-label={t("simulation.a11y")}>
      <h2 className={`${styles.title} ${collapsed ? styles.titleCollapsed : ""}`}>
        <button type="button" className={styles.titleBtn} aria-expanded={!collapsed} onClick={toggleCollapsed}>
          {t("simulation.title")}
          <span className={styles.caret} aria-hidden>{collapsed ? "▸" : "▾"}</span>
        </button>
      </h2>
      {!collapsed && (<>

      {/* Both what-ifs need exactly the same things to exist — a destination
          zone and a painted run — so they share one card rather than compete
          for the same column. */}
      {/* aria-pressed toggle buttons, matching LayerPanel's .preset control.
          NOT role="tab": without roving tabindex and a wired tabpanel that
          announces worse than a plain pressed button. */}
      <div className={styles.preset} role="group" aria-label={t("simulation.modeA11y")}>
        <button type="button" aria-pressed={mode === "relocation"}
                className={mode === "relocation" ? styles.btnActive : styles.btn}
                onClick={() => setMode("relocation")}>
          {t("simulation.modeRelocation")}
        </button>
        <button type="button" aria-pressed={mode === "taskMix"}
                className={mode === "taskMix" ? styles.btnActive : styles.btn}
                onClick={() => setMode("taskMix")}>
          {t("simulation.modeTaskMix")}
        </button>
      </div>
      {mode === "taskMix" ? (
        <TaskMixForm
          t={t} lang={lang} inputStyle={inputStyle} styles={styles}
          destinations={destinations} dockZoneId={destinationZoneId}
          onPickZone={setPickedZoneId}
          forklifts={forklifts} onForklifts={setForklifts}
          maxInterleaveM={maxInterleaveM} onMaxInterleaveM={setMaxInterleaveM}
          rateCap={rateCap} onRateCap={setRateCap}
          run={runTaskMix} result={taskMix} onClear={() => setTaskMix(null)}
        />
      ) : (
      <>
      {searchCount != null && (
        <label style={{ fontSize: 11, display: "flex", gap: 6, alignItems: "center", marginTop: 6 }}>
          <input type="checkbox" checked={useSearch}
                 onChange={(e) => setUseSearch(e.target.checked)} />
          {t("simulation.fromSearch", { count: searchCount })}
        </label>
      )}
      {!fromSearch && (
      <>
      <label style={{ fontSize: 11, display: "block", marginTop: 6 }}>{t("simulation.expiryBefore")}
        <input type="date" style={inputStyle} value={expiryBefore}
               onChange={(e) => setExpiryBefore(e.target.value)} />
      </label>
      <label style={{ fontSize: 11, display: "block", marginTop: 6 }}>{t("simulation.product")}
        <input style={{ ...inputStyle, fontFamily: "var(--font-mono)" }} value={product}
               placeholder={t("simulation.productHint")}
               onChange={(e) => setProduct(e.target.value)} />
      </label>
      </>
      )}
      <label style={{ fontSize: 11, display: "block", marginTop: 6 }}>{t("simulation.destination")}
        <select style={inputStyle} value={destinationZoneId}
                onChange={(e) => setPickedZoneId(e.target.value)}>
          <option value="">{t("simulation.destinationPick")}</option>
          {destinations.map((z) => (
            <option key={z.id} value={z.id}>{z.id} ({t(`zone.kind.${z.kind}`, { defaultValue: z.kind })})</option>
          ))}
        </select>
      </label>
      <label style={{ fontSize: 11, display: "block", marginTop: 6 }}>{t("simulation.forklifts")}
        <input style={{ ...inputStyle, width: 72, fontFamily: "var(--font-mono)" }} inputMode="numeric"
               value={forklifts} onChange={(e) => setForklifts(e.target.value)} />
      </label>
      <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
        <button type="button" className={styles.btn} disabled={!canRun}
                onClick={() => run.mutate()}>
          {run.isPending ? t("simulation.running") : t("simulation.run")}
        </button>
        {result && (
          <button type="button" className={styles.btn} onClick={() => clearSimulation()}>
            {t("simulation.clear")}
          </button>
        )}
      </div>
      {run.isError && (
        <p style={{ fontSize: 11, color: "var(--op-danger)", marginTop: 6 }}>
          {t("simulation.error", { msg: (run.error as Error).message })}
        </p>
      )}
      {result && (
        <dl className={styles.dl} style={{ marginTop: 8 }} data-testid="sim-result">
          <dt>{t("simulation.moves")}</dt>
          <dd className={styles.mono}>{result.moves}{result.shortfall > 0 && ` (${t("simulation.shortfall", { count: result.shortfall })})`}</dd>
          {result.unroutable > 0 && (
            <>
              <dt>{t("simulation.unroutable")}</dt>
              <dd className={styles.mono}>{result.unroutable}</dd>
            </>
          )}
          <dt>{t("simulation.cost")}</dt>
          <dd className={`${styles.mono} cost`}>{fmtMoney(result.cost, result.currency, lang)}</dd>
          <dt>{t("simulation.hours")}</dt>
          <dd className={styles.mono}>{fmtHours(result.forkliftHours * 3_600_000, lang)}</dd>
          <dt>{t("simulation.distance")}</dt>
          <dd className={styles.mono}>{fmtDistance(result.totalDistanceM, lang)}</dd>
          <dt>{t("simulation.makespan", { count: result.makespan.forklifts })}</dt>
          <dd className={styles.mono}>
            {fmtHours(result.makespan.hoursPerForklift * 3_600_000, lang)}
            {result.makespan.shiftDays != null && ` · ${t("simulation.shiftDays", { count: result.makespan.shiftDays })}`}
          </dd>
          {!result.speeds.configured && (
            <>
              <dt>{t("simulation.speeds")}</dt>
              <dd style={{ fontSize: 11 }}>
                {t("simulation.defaultSpeeds", {
                  loaded: fmtNum(result.speeds.loadedMps, lang, 1),
                  empty: fmtNum(result.speeds.emptyMps, lang, 1),
                })}
              </dd>
            </>
          )}
          {result.cost == null && (
            <>
              <dt>{t("simulation.noRate")}</dt>
              <dd style={{ fontSize: 11 }}>{t("simulation.noRateHint")}</dd>
            </>
          )}
        </dl>
      )}
      </>
      )}
      </>)}
    </aside>
  );
}

/**
 * ADR 305: the interleaving what-if. Prices the SAME task mix twice — one task
 * per trip versus a putaway paired with a nearby retrieval — so the two columns
 * differ in policy only.
 *
 * The panel deliberately shows the empty-distance drop and the cost drop as two
 * separate numbers. They are not the same claim: empty travel is fast and
 * handling time is untouched, so %vazia collapses much harder than money does.
 * A prospect who sees only the big number and then meets the small one has been
 * mis-sold; the note under the delta says so in one line.
 */
function TaskMixForm(props: {
  t: (k: string, o?: Record<string, unknown>) => string;
  lang: string;
  inputStyle: React.CSSProperties;
  styles: Record<string, string>;
  destinations: { id: string; kind: string }[];
  dockZoneId: string;
  onPickZone: (v: string) => void;
  forklifts: string;
  onForklifts: (v: string) => void;
  maxInterleaveM: string;
  onMaxInterleaveM: (v: string) => void;
  rateCap: string;
  onRateCap: (v: string) => void;
  run: { mutate: () => void; isPending: boolean; isError: boolean; error: unknown };
  result: TaskMixSim | null;
  onClear: () => void;
}) {
  const { t, lang, inputStyle, styles: s, destinations, dockZoneId, result } = props;
  const canRun = !!dockZoneId && !props.run.isPending;
  const pct = (v: number | null) => (v == null ? "—" : `${fmtNum(v, lang, 1)} %`);

  return (
    <>
      <label style={{ fontSize: 11, display: "block", marginTop: 6 }}>{t("simulation.dock")}
        <select style={inputStyle} value={dockZoneId} onChange={(e) => props.onPickZone(e.target.value)}>
          <option value="">{t("simulation.destinationPick")}</option>
          {destinations.map((z) => (
            <option key={z.id} value={z.id}>{z.id} ({t(`zone.kind.${z.kind}`, { defaultValue: z.kind })})</option>
          ))}
        </select>
      </label>
      <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
        <label style={{ fontSize: 11, flex: 1 }}>{t("simulation.maxInterleave")}
          <input style={{ ...inputStyle, fontFamily: "var(--font-mono)" }} inputMode="decimal"
                 value={props.maxInterleaveM} onChange={(e) => props.onMaxInterleaveM(e.target.value)} />
        </label>
        <label style={{ fontSize: 11, flex: 1 }}>{t("simulation.rateCap")}
          <input style={{ ...inputStyle, fontFamily: "var(--font-mono)" }} inputMode="decimal"
                 placeholder={t("simulation.rateCapHint")}
                 value={props.rateCap} onChange={(e) => props.onRateCap(e.target.value)} />
        </label>
      </div>
      <label style={{ fontSize: 11, display: "block", marginTop: 6 }}>{t("simulation.forklifts")}
        <input style={{ ...inputStyle, width: 72, fontFamily: "var(--font-mono)" }} inputMode="numeric"
               value={props.forklifts} onChange={(e) => props.onForklifts(e.target.value)} />
      </label>
      <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
        <button type="button" className={s.btn} disabled={!canRun} onClick={() => props.run.mutate()}>
          {props.run.isPending ? t("simulation.running") : t("simulation.run")}
        </button>
        {result && (
          <button type="button" className={s.btn} onClick={props.onClear}>{t("simulation.clear")}</button>
        )}
      </div>
      {props.run.isError && (
        <p style={{ fontSize: 11, color: "var(--op-danger)", marginTop: 6 }}>
          {t("simulation.error", { msg: (props.run.error as Error).message })}
        </p>
      )}
      {result && (
        <div data-testid="taskmix-result">
          <div className={s.policies}>
            <div className={s.policy}>
              <p className={s.policyHead}>{t("simulation.single")}</p>
              <span className={s.policyBig}>{pct(result.singleCommand.emptyDistancePct)}</span>
              <span className={s.policySub}>{t("simulation.emptyShare")}</span>
              <span className={s.policySub} style={{ marginTop: 4 }}>
                <strong className="cost">{fmtMoney(result.singleCommand.costPerPallet, result.currency, lang)}</strong>/{t("simulation.pallet")}
              </span>
            </div>
            <div className={`${s.policy} ${s.policyWin}`}>
              <p className={s.policyHead}>{t("simulation.dual")}</p>
              <span className={s.policyBig}>{pct(result.dualCommand.emptyDistancePct)}</span>
              <span className={s.policySub}>{t("simulation.emptyShare")}</span>
              <span className={s.policySub} style={{ marginTop: 4 }}>
                <strong className="cost">{fmtMoney(result.dualCommand.costPerPallet, result.currency, lang)}</strong>/{t("simulation.pallet")}
              </span>
            </div>
          </div>
          <p className={s.deltaNote}>{t("simulation.twoNumbers")}</p>
          <dl className={s.dl} style={{ marginTop: 8 }}>
            <dt>{t("simulation.tasks")}</dt>
            <dd className={s.mono}>
              {t("simulation.taskSplit", {
                putaway: result.tasks.putaway, retrieval: result.tasks.retrieval,
              })}
            </dd>
            <dt>{t("simulation.interleaved")}</dt>
            <dd className={s.mono}>
              {fmtNum(result.dualCommand.achievedInterleaveRate * 100, lang, 0)} %
              {result.dualCommand.unpaired.reason && (
                <span className={s.policySub}>
                  {t(`simulation.unpaired.${result.dualCommand.unpaired.reason}`)}
                </span>
              )}
            </dd>
            <dt>{t("simulation.saving")}</dt>
            <dd className={s.mono}>
              {result.delta.secondsPerPalletPct == null
                ? "—" : `${fmtNum(result.delta.secondsPerPalletPct, lang, 1)} %`}
              <span className={s.policySub}>
                {fmtHours(Math.abs(result.delta.forkliftHours) * 3_600_000, lang)}
                {result.delta.cost != null && <> · <strong className="cost">{fmtMoney(result.delta.cost, result.currency, lang)}</strong></>}
              </span>
            </dd>
            <dt>{t("simulation.fleetEquivalent")}</dt>
            <dd className={s.mono}>
              {t("simulation.fleetArrow", {
                from: result.fleet.current, to: result.fleet.equivalent,
              })}
              {/* ADR 305 / ADR 291: capacity arithmetic on AVERAGE work. A fleet
                  is sized by its peak, so this is never a staffing decision. */}
              <span className={s.policySub}>{t("simulation.fleetBasis")}</span>
            </dd>
            <dt>{t("simulation.distance")}</dt>
            <dd className={s.mono}>
              {fmtDistance(result.dualCommand.totalDistanceM, lang)}
              <span className={s.policySub}>
                {t("simulation.wasDistance", {
                  v: fmtDistance(result.singleCommand.totalDistanceM, lang),
                })}
              </span>
            </dd>
            {result.unroutable > 0 && (
              <>
                <dt>{t("simulation.unroutable")}</dt>
                <dd className={s.mono}>{result.unroutable}</dd>
              </>
            )}
            {!result.speeds.configured && (
              <>
                <dt>{t("simulation.speeds")}</dt>
                <dd style={{ fontSize: 11 }}>
                  {t("simulation.defaultSpeeds", {
                    loaded: fmtNum(result.speeds.loadedMps, lang, 1),
                    empty: fmtNum(result.speeds.emptyMps, lang, 1),
                  })}
                </dd>
              </>
            )}
            {result.singleCommand.cost == null && (
              <>
                <dt>{t("simulation.noRate")}</dt>
                <dd style={{ fontSize: 11 }}>{t("simulation.noRateHint")}</dd>
              </>
            )}
          </dl>
        </div>
      )}
    </>
  );
}
